import { RouteConfig } from 'vue-router'

import { Role } from '@/models/role'
import classMatcSystemIconActive from '../assets/images/icon_menu_class_match_active@2x.png'
import classMatcSystemIconInactive from '../assets/images/icon_menu_class_match_inactive@2x.png'

const Layout = () => import('../views/Layout.vue')
const LayoutEmpty = () => import('../views/LayoutEmpty.vue')
const MatchManageIndex = () => import('../views/match-system/match-manage/match-list/Index.vue')
const ProjectLibraryIndex = () => import('../views/match-system/match-manage/project-library/Index.vue')
const VenueManageIndex = () => import('../views/match-system/venue-manage/venue-list/Index.vue')
const JudgeManageIndex = () => import('../views/match-system/judge-manage/judge-list/Index.vue')
const RegistrationManageIndex = () => import('../views/match-system/registration-manage/Index.vue')
const ScheduleManageIndex = () => import('../views/match-system/schedule-manage/Index.vue')
const ScoresManageIndex = () => import('../views/match-system/scores-manage/Index.vue')

type MatchSystemNamePrefix = 'match_system' | 'school_match_system'

export interface IMatchSystemRouteOptions {
  namePrefix: MatchSystemNamePrefix
  inSchool?: boolean
  roles?: Role[]
}

function activeMenu(_namePrefix: MatchSystemNamePrefix, _suffix: string) {
  return `${_namePrefix}_${_suffix}`
}

function leafProps(_inSchool?: boolean) {
  return _inSchool ? { inSchool: true } : undefined
}

function roleMeta(_roles?: Role[]) {
  return _roles?.length ? { roles: _roles } : {}
}

function inSchoolMeta(_inSchool?: boolean) {
  return _inSchool ? { inSchool: true } : {}
}

export function buildMatchSystemChildRoutes(_options: IMatchSystemRouteOptions): RouteConfig[] {
  const { namePrefix, inSchool, roles } = _options
  const _roleMeta = roleMeta(roles)
  const _inSchoolMeta = inSchoolMeta(inSchool)
  const _props = leafProps(inSchool)

  const matchManageRoute: RouteConfig = {
    path: 'match-manage',
    component: LayoutEmpty,
    redirect: { name: `${namePrefix}_match_manage` },
    children: [
      {
        path: '',
        name: `${namePrefix}_match_manage`,
        component: MatchManageIndex,
        props: _props,
        meta: {
          title: '赛事管理',
          showMenu: true,
          activeMenu: activeMenu(namePrefix, 'match_manage'),
          ..._roleMeta,
          ..._inSchoolMeta,
        },
      },
      {
        path: 'match-list',
        name: `${namePrefix}_match_list`,
        redirect: { name: `${namePrefix}_match_manage` },
      },
      {
        path: 'project-library',
        redirect: { name: `${namePrefix}_project_library` },
      },
      {
        path: 'match-detail/:id',
        name: `${namePrefix}_match_detail`,
        component: () => import('../views/match-system/match-manage/match-detail/Index.vue'),
        props: _props,
        meta: {
          title: '赛事详情',
          showMenu: false,
          activeMenu: activeMenu(namePrefix, 'match_manage'),
          ..._inSchoolMeta,
        },
      },
    ],
  }

  const projectLibraryRoute: RouteConfig = {
    path: 'project-library',
    name: `${namePrefix}_project_library`,
    component: ProjectLibraryIndex,
    props: _props,
    meta: {
      title: '项目库',
      showMenu: true,
      activeMenu: activeMenu(namePrefix, 'project_library'),
      ..._roleMeta,
      ..._inSchoolMeta,
    },
  }

  const venueManageRoute: RouteConfig = {
    path: 'venue-manage',
    name: `${namePrefix}_venue_manage`,
    component: VenueManageIndex,
    props: _props,
    meta: {
      title: '场地管理',
      showMenu: true,
      activeMenu: activeMenu(namePrefix, 'venue_manage'),
      ..._roleMeta,
      ..._inSchoolMeta,
    },
  }

  const judgeManageRoute: RouteConfig = {
    path: 'judge-manage',
    name: `${namePrefix}_judge_manage`,
    component: JudgeManageIndex,
    props: _props,
    meta: {
      title: '裁判管理',
      showMenu: true,
      activeMenu: activeMenu(namePrefix, 'judge_manage'),
      ..._roleMeta,
      ..._inSchoolMeta,
    },
  }

  const registrationManageRoute: RouteConfig = {
    path: 'registration-manage',
    name: `${namePrefix}_registration_manage`,
    component: RegistrationManageIndex,
    props: _props,
    meta: {
      title: '报名管理',
      showMenu: true,
      activeMenu: activeMenu(namePrefix, 'registration_manage'),
      ..._roleMeta,
      ..._inSchoolMeta,
    },
  }

  const scheduleManageRoute: RouteConfig = {
    path: 'schedule-manage',
    name: `${namePrefix}_schedule_manage`,
    component: ScheduleManageIndex,
    props: _props,
    meta: {
      title: '赛事编排',
      showMenu: true,
      activeMenu: activeMenu(namePrefix, 'schedule_manage'),
      ..._roleMeta,
      ..._inSchoolMeta,
    },
  }

  const scoresManageRoute: RouteConfig = {
    path: 'scores-manage',
    name: `${namePrefix}_scores_manage`,
    component: ScoresManageIndex,
    props: _props,
    meta: {
      title: '成绩管理',
      showMenu: true,
      activeMenu: activeMenu(namePrefix, 'scores_manage'),
      ..._roleMeta,
      ..._inSchoolMeta,
    },
  }

  return [
    matchManageRoute,
    projectLibraryRoute,
    venueManageRoute,
    judgeManageRoute,
    registrationManageRoute,
    scheduleManageRoute,
    scoresManageRoute,
  ]
}

const organizationOptions: IMatchSystemRouteOptions = {
  namePrefix: 'match_system',
  roles: [Role.CompetitionManager],
}

const schoolOptions: IMatchSystemRouteOptions = {
  namePrefix: 'school_match_system',
  inSchool: true,
}

export const organizationMatchSystemRoutes: RouteConfig[] = [
  {
    path: 'match-system',
    name: 'match_system',
    component: LayoutEmpty,
    redirect: { name: 'match_system_match_manage' },
    meta: {
      title: '赛事管理系统',
      icon: classMatcSystemIconInactive,
      iconActive: classMatcSystemIconActive,
      showMenu: true,
      roles: [Role.CompetitionManager],
    },
    children: buildMatchSystemChildRoutes(organizationOptions),
  },
]

export const schoolMatchSystemRoutes: RouteConfig[] = [
  {
    path: '/match-system',
    name: 'school_match_system',
    component: Layout,
    redirect: { name: 'school_match_system_match_manage' },
    meta: {
      title: '赛事管理系统',
      icon: classMatcSystemIconInactive,
      iconActive: classMatcSystemIconActive,
      showMenu: true,
      inSchool: true,
    },
    children: buildMatchSystemChildRoutes(schoolOptions),
  },
]

export default organizationMatchSystemRoutes
